import React, { useState, useEffect } from 'react';
import { Modal } from 'antd';
import './markCompleteModal.scss';

const MAX_FIELD_LENGTH = 500;

interface MarkCompleteModalProps {
    visible: boolean;
    onClose: () => void;
    // Return false (or a Promise resolving to false) to keep the modal open on failure
    onConfirm: (rootCause: string, resolutionDetails: string) => Promise<boolean | void> | boolean | void;
    // Root cause is only mandatory on a critical incident; a routine one can be
    // closed with just the resolution details. Matches the API rule.
    isCritical?: boolean;
}

const MarkCompleteModal: React.FC<MarkCompleteModalProps> = ({
    visible,
    onClose,
    onConfirm,
    isCritical = false,
}) => {
    const [rootCause, setRootCause] = useState('');
    const [resolutionDetails, setResolutionDetails] = useState('');
    const [rootCauseError, setRootCauseError] = useState('');
    const [resolutionError, setResolutionError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (visible) {
            setRootCause('');
            setResolutionDetails('');
            setRootCauseError('');
            setResolutionError('');
            setIsSubmitting(false);
        }
    }, [visible]);

    const handleConfirm = async () => {
        if (isSubmitting) return;

        let valid = true;
        if (isCritical && !rootCause.trim()) {
            setRootCauseError('Root cause is required.');
            valid = false;
        } else if (rootCause.length > MAX_FIELD_LENGTH) {
            setRootCauseError(`Root cause must not exceed ${MAX_FIELD_LENGTH} characters.`);
            valid = false;
        }
        if (!resolutionDetails.trim()) {
            setResolutionError('Resolution details are required.');
            valid = false;
        } else if (resolutionDetails.length > MAX_FIELD_LENGTH) {
            setResolutionError(`Resolution details must not exceed ${MAX_FIELD_LENGTH} characters.`);
            valid = false;
        }
        if (!valid) return;

        setIsSubmitting(true);
        try {
            const success = await Promise.resolve(onConfirm(rootCause, resolutionDetails));
            if (success !== false) onClose();
        } catch {
            // Confirm threw — modal stays open so the user can retry
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            open={visible}
            onCancel={() => { if (!isSubmitting) onClose(); }}
            footer={null}
            width={560}
            className="mark-complete-modal"
            centered>
            <div className="mark-complete-container">
                <h2 className="heading">Mark as Complete</h2>
                <p className="description">
                    Are you sure you want to mark this incident as complete? You can reopen it later if it was closed in error.
                </p>

                <div className="resolution-field">
                    <h3>{isCritical ? 'Root Cause' : 'Root Cause (optional)'}</h3>
                    <textarea
                        className={`resolution-textarea${rootCauseError ? ' input-error' : ''}`}
                        value={rootCause}
                        maxLength={MAX_FIELD_LENGTH}
                        onChange={(e) => { setRootCause(e.target.value); setRootCauseError(''); }} />
                    {rootCauseError && <span className="field-error">{rootCauseError}</span>}
                </div>

                <div className="resolution-field">
                    <h3>Resolution Details</h3>
                    <textarea
                        className={`resolution-textarea${resolutionError ? ' input-error' : ''}`}
                        value={resolutionDetails}
                        maxLength={MAX_FIELD_LENGTH}
                        onChange={(e) => { setResolutionDetails(e.target.value); setResolutionError(''); }} />
                    {resolutionError && <span className="field-error">{resolutionError}</span>}
                </div>

                <div className="button-wrapper">
                    <button type="button" className="confirm-button" onClick={handleConfirm} disabled={isSubmitting}>
                        {isSubmitting ? 'Submitting...' : 'Confirm'}
                    </button>
                    <button type="button" className="cancel-button" onClick={onClose} disabled={isSubmitting}>
                        Cancel
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default MarkCompleteModal;
